import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["appHub", "children", "withoutSidebar", "onReady", "onContentReady", "onContentError", "iframeContainerID"],
  _excluded2 = ["handleMessage", "show"],
  _excluded3 = ["handleMessage", "show"],
  _excluded4 = ["handleMessage", "show"],
  _excluded5 = ["show"];
import { c as _c } from "react-compiler-runtime";
import I18n from 'I18n';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CopilotProvider from 'copilot-toolkit/CopilotProvider';
import CollaborationSidebarStateContext from '../contexts/CollaborationSidebarStateContext';
import UserSettingsProvider from '../contexts/UserSettingsProvider';
import usePersistPropsToCollaborationSidebarState from '../hooks/usePersistPropsToCollaborationSidebarState';
import { showWarningNotification } from '../utils/notifications';
import { getIsCommentingUrl } from '../utils/urls';
import CommentModeIndicator from 'collaboration-sidebar/features/deepCommenting/components/CommentModeIndicator';
import CollaborationSidebarPropsContext from '../contexts/CollaborationSidebarPropsContext';
import useEmbeddedComments from '../features/deepCommenting/hooks/useEmbeddedComments';
import useHasUnreadComments from '../hooks/useHasUnreadComments';
import useSidebarIframeHandlers from '../hooks/useSidebarIframeHandlers';
import useConfirmDeleteCommentModal from 'collaboration-sidebar/hooks/modals/useConfirmDeleteCommentModal';
import useNotificationSettingsModal from 'collaboration-sidebar/hooks/modals/useNotificationSettingsModal';
import useTaskDeleteModal from 'collaboration-sidebar/hooks/modals/useTaskDeleteModal';
import { useConfirmEditRepeatingTaskModal } from 'collaboration-sidebar-common/features/repeatingTasks/hooks/useConfirmEditRepeatingTaskModal';
import { useEventCallback } from 'collaboration-sidebar-common/hooks/useEventCallback';
import { SUPPORTED_HUBS } from 'collaboration-sidebar/constants/supportedHubs';
import useSidebarOpenCloseHandlers from '../hooks/useSidebarOpenCloseHandlers';
import useHasSidebarPermission from '../hooks/useHasSidebarPermission';
import AsyncMessageHandlers from './MessageHandlers/AsyncMessageHandlers';
import Modals from './Modals';
import EmbeddedCommentSetup from './EmbeddedCommentSetup';
import useCollaborationSidebarState from '../hooks/useCollaborationSidebarState';
import { trackInteraction } from '../utils/usageTracker';
import useGetPropsFromFAS from '../features/fas/hooks/useGetPropsFromFAS';
import useStableReferences from '../hooks/useStableReferences';
import ErrorCountWrapper from './errors/ErrorCountWrapper';
import { SidebarMessageHandler } from '../features/sidebar/SidebarMessageHandler';
import DevtoolsLoader from 'collaboration-sidebar-common/features/devtools/components/DevtoolsLoader';
import useCollaborationSidebarDataFetchingClient from '../hooks/useCollaborationSidebarDataFetchingClient';
import { DataFetchingClientProvider } from 'data-fetching-client';
import { Metrics } from '../utils/metrics';
import { makeCollaborationSidebarStateLeader } from '../store/collaborationSidebarState';
import { CollaborationSidebarProvider, useCollaborationSidebarActions, useCollaborationSidebarStateShallowValue, useCollaborationSidebarValue, useStateValue } from '../store/collaborationSidebarProvider';
import { getInitialCollaborationSidebarState, mergeCollaborationSidebarState } from '../utils/collaborationSidebarState';
import isEqual from 'hs-lodash/isEqual';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function useSidebarInitMetrics(messageHandlersReady, appName, objectType, withoutSidebar) {
  const initStartTimeRef = useRef(null);
  useEffect(() => {
    if (initStartTimeRef.current === null) {
      initStartTimeRef.current = performance.now();
    }
  }, []);
  useEffect(() => {
    if (messageHandlersReady && initStartTimeRef.current && appName && objectType) {
      const duration = performance.now() - initStartTimeRef.current;
      Metrics.timer('sidebar-init-duration', {
        app_name: appName,
        object_type: objectType,
        has_sidebar: String(!withoutSidebar)
      }).update(duration);
      initStartTimeRef.current = null;
    }
  }, [messageHandlersReady, appName, objectType, withoutSidebar]);
}
function ControlledCollaborationSidebarProvider(_ref) {
  'use no memo';

  var _fasProps2, _fasProps3, _fasProps4;
  let {
      appHub: appHubProp,
      children,
      withoutSidebar: withoutSidebarProp,
      onReady,
      onContentReady,
      onContentError,
      iframeContainerID
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    collaborationSidebarState,
    onUpdateCollaborationSidebarState
  } = useCollaborationSidebarState();

  /**
   * Keep backwards compatibility by providing default values for FAS props when not ungated or when FAS is available.
   */
  const defaultFasProps = useMemo(() => ({
    appHub: appHubProp && Array.isArray(appHubProp) ? appHubProp : appHubProp && [SUPPORTED_HUBS[appHubProp]],
    defaultSidebarPlacement: rest.defaultSidebarPlacement,
    dragAndDropAreas: rest.dragAndDropAreas,
    enabledFeatures: rest.enabledFeatures,
    hideCommentModeAndToggle: rest.hideCommentModeAndToggle,
    isListingPage: rest.isListingPage,
    isOverlay: rest.isOverlay,
    objectType: rest.skipFas ? rest.objectType : undefined,
    scrollablePage: rest.scrollablePage,
    showSidebarHeader: rest.showSidebarHeader,
    withoutSidebar: withoutSidebarProp
  }), [appHubProp, rest.defaultSidebarPlacement, rest.dragAndDropAreas, rest.enabledFeatures, rest.hideCommentModeAndToggle, rest.isListingPage, rest.isOverlay, rest.objectType, rest.scrollablePage, rest.showSidebarHeader, rest.skipFas, withoutSidebarProp]);
  let fasProps = defaultFasProps;

  /**
   * Read from Framework App Settings.
   * If not available, fallback to provided props.
   */
  const {
    fasProps: tempProps,
    loading
  } = useGetPropsFromFAS({
    appName: rest.appName,
    enabledFeatures: rest.enabledFeatures,
    skipFas: rest.skipFas
  });
  if (tempProps) {
    fasProps = tempProps;
  }
  const didEmitOnReady = useRef(false);
  const hasShownNoPermissionWarning = useRef(false);
  const pageContentRef = useRef(null);
  const sendMessageRef = useRef(undefined);
  const onMessageRef = useRef(() => {});
  const [messageHandlersReady, setMessageHandlersReady] = useState(false);

  /**
   * Tracks all usages of collaboration-sidebar by app name and object type
   */
  useEffect(() => {
    var _fasProps;
    trackInteraction({
      action: 'render collaboration-sidebar',
      applicationName: rest.appName,
      objectType: (_fasProps = fasProps) === null || _fasProps === void 0 ? void 0 : _fasProps.objectType
    });
  }, [(_fasProps2 = fasProps) === null || _fasProps2 === void 0 ? void 0 : _fasProps2.objectType, rest.appName]);
  useSidebarInitMetrics(messageHandlersReady, rest.appName, (_fasProps3 = fasProps) === null || _fasProps3 === void 0 ? void 0 : _fasProps3.objectType, fasProps.withoutSidebar);
  useEffect(() => {
    if (messageHandlersReady && onReady && !didEmitOnReady.current) {
      didEmitOnReady.current = true;
      onReady();
    }
  }, [messageHandlersReady, onReady]);
  const {
    objectId,
    onCommentsUpdated
  } = rest;
  const expandableSidebarRef = useRef(null);
  const isOpen = useStateValue('isOpen');
  const iframeHandlerProps = useSidebarIframeHandlers({
    enabledFeatures: fasProps.enabledFeatures,
    isOpen,
    onContentError,
    onContentReady,
    sendMessageRef,
    withoutSidebar: fasProps.withoutSidebar,
    appName: rest.appName,
    objectType: (_fasProps4 = fasProps) === null || _fasProps4 === void 0 ? void 0 : _fasProps4.objectType
  });
  const hasSidebarPermission = useHasSidebarPermission({
    appHub: fasProps.appHub,
    enabledFeatures: fasProps.enabledFeatures
  });
  useEffect(() => {
    if (!hasShownNoPermissionWarning.current && !hasSidebarPermission && getIsCommentingUrl() && !loading) {
      showWarningNotification({
        title: I18n.text('collaborationSidebar.notifications.failure.missingPermissionCommentingTitle'),
        message: I18n.text('collaborationSidebar.notifications.failure.missingPermissionCommentingBody'),
        sticky: true
      });
      hasShownNoPermissionWarning.current = true;
    }
  }, [hasSidebarPermission, loading]);
  const {
    hasUnreadComments,
    unreadCommentsCount,
    markCommentAsRead,
    refetchComments
  } = useHasUnreadComments(loading ? undefined : fasProps.objectType, objectId);
  const isCommentModeActive = useStateValue('isCommentModeActive');
  const actions = useCollaborationSidebarActions();
  const {
    handleClose
  } = useSidebarOpenCloseHandlers();
  const {
    startCommentMode,
    cancelCommentMode
  } = useEmbeddedComments({
    moduleId: null,
    collaborationSidebarState,
    onUpdateCollaborationSidebarState
  });
  const _useConfirmDeleteComm = useConfirmDeleteCommentModal({
      sendMessageRef,
      isSendMessageReady: iframeHandlerProps.isReady
    }),
    {
      handleMessage: handleConfirmDeleteCommentModalMessage,
      show: showDeleteCommentModal
    } = _useConfirmDeleteComm,
    confirmDeleteCommentModalProps = _objectWithoutPropertiesLoose(_useConfirmDeleteComm, _excluded2);
  const _useNotificationSetti = useNotificationSettingsModal({
      sendMessageRef,
      isSendMessageReady: iframeHandlerProps.isReady
    }),
    {
      handleMessage: handleNotificationSettingsModalMessage,
      show: showNotificationSettingsModal
    } = _useNotificationSetti,
    notificationSettingsModalProps = _objectWithoutPropertiesLoose(_useNotificationSetti, _excluded3);
  const _useTaskDeleteModal = useTaskDeleteModal({
      sendMessageRef,
      isSendMessageReady: iframeHandlerProps.isReady
    }),
    {
      handleMessage: handleTaskUpdatePanelMessage,
      show: showTaskDeleteModal
    } = _useTaskDeleteModal,
    taskDeleteModalProps = _objectWithoutPropertiesLoose(_useTaskDeleteModal, _excluded4);
  const _useConfirmEditRepeat = useConfirmEditRepeatingTaskModal(),
    {
      show: showConfirmEditRepeatingTaskModal
    } = _useConfirmEditRepeat,
    confirmEditRepeatingTaskModalProps = _objectWithoutPropertiesLoose(_useConfirmEditRepeat, _excluded5);
  const [isPageContentRefSet, setIsPageContentRefSet] = useState(false);
  const collaborationSidebarProviderProps = Object.assign({}, rest, {
    pageContentRef,
    isPageContentRefSet,
    setIsPageContentRefSet,
    hasSidebarPermission,
    expandableSidebarRef,
    hasUnreadComments,
    unreadCommentsCount,
    isModalOpen: showDeleteCommentModal || showTaskDeleteModal || showConfirmEditRepeatingTaskModal,
    iframe: iframeHandlerProps,
    onMessageRef,
    messageHandlersReady,
    startCommentMode,
    cancelCommentMode
  }, fasProps);
  return (
    /*#__PURE__*/
    /**
     * Creating a layer here makes sure that if the collaboration sidebar is
     * rendered in an iframe the copilot it uses will only be initialized for
     * the iframe context it's rendered in
     */
    _jsxs(CopilotProvider, {
      createLayer: true,
      children: [/*#__PURE__*/_jsx(AsyncMessageHandlers, {
        handleTaskUpdatePanelMessage: handleTaskUpdatePanelMessage,
        handleConfirmDeleteCommentModalMessage: handleConfirmDeleteCommentModalMessage,
        handleNotificationSettingsModalMessage: handleNotificationSettingsModalMessage,
        markCommentAsRead: markCommentAsRead,
        handleClose: handleClose,
        refetchComments: refetchComments,
        onCommentsUpdated: onCommentsUpdated,
        preLoadIframe: iframeHandlerProps.preLoadIframe,
        sendMessageRef: sendMessageRef,
        onMessageRef: onMessageRef,
        setMessageHandlersReady: setMessageHandlersReady
      }), /*#__PURE__*/_jsx(CollaborationSidebarPropsContext.Provider, {
        value: collaborationSidebarProviderProps,
        children: /*#__PURE__*/_jsxs(UserSettingsProvider, {
          objectId: objectId,
          children: [/*#__PURE__*/_jsx(SidebarMessageHandler, {}), /*#__PURE__*/_jsx(EmbeddedCommentSetup, {}), /*#__PURE__*/_jsx(DevtoolsLoader, {}), /*#__PURE__*/_jsx(Modals, {
            confirmDeleteCommentModalProps: confirmDeleteCommentModalProps,
            taskDeleteModalProps: taskDeleteModalProps,
            confirmEditRepeatingTaskModalProps: confirmEditRepeatingTaskModalProps,
            notificationSettingsModalProps: notificationSettingsModalProps,
            showDeleteCommentModal: showDeleteCommentModal,
            showNotificationSettingsModal: showNotificationSettingsModal,
            showTaskDeleteModal: showTaskDeleteModal,
            showConfirmEditRepeatingTaskModal: showConfirmEditRepeatingTaskModal,
            withoutSidebar: !!fasProps.withoutSidebar,
            isReady: messageHandlersReady,
            IFrameError: iframeHandlerProps.IFrameError,
            iframeContainerID: iframeContainerID
          }), children, isCommentModeActive && /*#__PURE__*/_jsx(CommentModeIndicator, {
            onCancel: () => {
              actions.disableCommentMode();
              trackInteraction({
                action: 'close comment mode'
              });
            }
          })]
        })
      })]
    })
  );
}
const STABLE_REFERENCE_KEYS = ['enabledFeatures', 'appHub'];
const useSyncLeaderStore = t0 => {
  "use memo";

  const $ = _c(16);
  const {
    collaborationSidebarState,
    onUpdateCollaborationSidebarState
  } = t0;
  const leaderStore = useCollaborationSidebarValue("collaborationSidebarState");
  const collaborationSidebarStateRef = useRef(collaborationSidebarState);
  let t1;
  if ($[0] !== collaborationSidebarState) {
    t1 = () => {
      collaborationSidebarStateRef.current = collaborationSidebarState;
    };
    $[0] = collaborationSidebarState;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useEffect(t1);
  const shouldSyncToExternalState = Boolean(onUpdateCollaborationSidebarState);
  let t2;
  if ($[2] !== leaderStore.actions || $[3] !== onUpdateCollaborationSidebarState) {
    t2 = updates => {
      onUpdateCollaborationSidebarState === null || onUpdateCollaborationSidebarState === void 0 || onUpdateCollaborationSidebarState(updates);
      leaderStore.actions.onUpdateCollaborationSidebarState(updates);
    };
    $[2] = leaderStore.actions;
    $[3] = onUpdateCollaborationSidebarState;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleUpdate = t2;
  const onUpdateCollaborationSidebarStateEvent = useEventCallback(onUpdateCollaborationSidebarState);
  let t3;
  let t4;
  if ($[5] !== leaderStore || $[6] !== onUpdateCollaborationSidebarStateEvent || $[7] !== shouldSyncToExternalState) {
    t3 = () => {
      if (!shouldSyncToExternalState) {
        return;
      }
      const cleanup = leaderStore.subscribe(newValue => {
        const mergedState = collaborationSidebarStateRef.current ? mergeCollaborationSidebarState(collaborationSidebarStateRef.current, newValue) : newValue;
        if (isEqual(mergedState, collaborationSidebarStateRef.current)) {
          return;
        }
        onUpdateCollaborationSidebarStateEvent(mergedState);
      });
      return cleanup;
    };
    t4 = [leaderStore, onUpdateCollaborationSidebarStateEvent, shouldSyncToExternalState];
    $[5] = leaderStore;
    $[6] = onUpdateCollaborationSidebarStateEvent;
    $[7] = shouldSyncToExternalState;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  const previousCollaborationSidebarStateRef = useRef(collaborationSidebarState);
  let t5;
  let t6;
  if ($[10] !== collaborationSidebarState || $[11] !== leaderStore) {
    t5 = () => {
      const prev = previousCollaborationSidebarStateRef.current;
      previousCollaborationSidebarStateRef.current = collaborationSidebarState;
      const currentLeaderState = leaderStore.getState();
      if (!collaborationSidebarState || isEqual(collaborationSidebarState, currentLeaderState)) {
        return;
      }
      const updates_0 = {};
      let hasUpdates = false;
      for (const key of Object.keys(collaborationSidebarState)) {
        const typedKey = key;
        if (collaborationSidebarState[typedKey] !== (prev === null || prev === void 0 ? void 0 : prev[typedKey]) && collaborationSidebarState[typedKey] !== currentLeaderState[typedKey] && (prev === null || prev === void 0 ? void 0 : prev[typedKey]) === currentLeaderState[typedKey]) {
          updates_0[typedKey] = collaborationSidebarState[typedKey];
          hasUpdates = true;
        }
      }
      if (!hasUpdates) {
        return;
      }
      leaderStore.actions.onUpdateCollaborationSidebarState(updates_0);
    };
    t6 = [collaborationSidebarState, leaderStore];
    $[10] = collaborationSidebarState;
    $[11] = leaderStore;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  if ($[14] !== handleUpdate) {
    t7 = {
      handleUpdate
    };
    $[14] = handleUpdate;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
function CollaborationSidebarProviderWithState(props) {
  "use memo";

  const $ = _c(19);
  const {
    collaborationSidebarState,
    onUpdateCollaborationSidebarState
  } = props;
  let t0;
  if ($[0] !== collaborationSidebarState || $[1] !== onUpdateCollaborationSidebarState) {
    t0 = {
      collaborationSidebarState,
      onUpdateCollaborationSidebarState
    };
    $[0] = collaborationSidebarState;
    $[1] = onUpdateCollaborationSidebarState;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    handleUpdate
  } = useSyncLeaderStore(t0);
  let t1;
  if ($[3] !== props) {
    t1 = {
      object: props,
      keys: STABLE_REFERENCE_KEYS
    };
    $[3] = props;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const stableProps = useStableReferences(t1);
  let t2;
  if ($[5] !== handleUpdate || $[6] !== stableProps) {
    t2 = {
      onUpdateCollaborationSidebarState: handleUpdate,
      collaborationSidebarProps: stableProps
    };
    $[5] = handleUpdate;
    $[6] = stableProps;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const persistedUpdates = usePersistPropsToCollaborationSidebarState(t2);
  const leaderState = useCollaborationSidebarStateShallowValue(_temp);
  const t3 = collaborationSidebarState !== null && collaborationSidebarState !== void 0 ? collaborationSidebarState : leaderState;
  let t4;
  if ($[8] !== persistedUpdates || $[9] !== t3) {
    t4 = mergeCollaborationSidebarState(t3, persistedUpdates);
    $[8] = persistedUpdates;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== handleUpdate || $[12] !== t4) {
    t5 = {
      collaborationSidebarState: t4,
      onUpdateCollaborationSidebarState: handleUpdate
    };
    $[11] = handleUpdate;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const contextValue = t5;
  let t6;
  if ($[14] !== stableProps) {
    t6 = /*#__PURE__*/_jsx(ControlledCollaborationSidebarProvider, Object.assign({}, stableProps));
    $[14] = stableProps;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== contextValue || $[17] !== t6) {
    t7 = /*#__PURE__*/_jsx(CollaborationSidebarStateContext.Provider, {
      value: contextValue,
      children: t6
    });
    $[16] = contextValue;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}
function _temp(state) {
  return state;
}
export default function CollaborationSidebarProviderWithErrorCount(props) {
  "use memo";

  const $ = _c(16);
  const dataFetchingClient = useCollaborationSidebarDataFetchingClient();
  let t0;
  if ($[0] !== props.children) {
    t0 = () => props.children;
    $[0] = props.children;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.collaborationSidebarState) {
    t1 = () => {
      var _props$collaborationS;
      return {
        collaborationSidebarState: makeCollaborationSidebarStateLeader((_props$collaborationS = props.collaborationSidebarState) !== null && _props$collaborationS !== void 0 ? _props$collaborationS : getInitialCollaborationSidebarState())
      };
    };
    $[2] = props.collaborationSidebarState;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props) {
    t2 = /*#__PURE__*/_jsx(CollaborationSidebarProviderWithState, Object.assign({}, props));
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = /*#__PURE__*/_jsx(CollaborationSidebarProvider, {
      setup: t1,
      teardown: _temp2,
      children: t2
    });
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== dataFetchingClient || $[10] !== t3) {
    t4 = /*#__PURE__*/_jsx(DataFetchingClientProvider, {
      client: dataFetchingClient,
      children: t3
    });
    $[9] = dataFetchingClient;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== props.appName || $[13] !== t0 || $[14] !== t4) {
    t5 = /*#__PURE__*/_jsx(ErrorCountWrapper, {
      appName: props.appName,
      errorBoundaryFingerprint: "CollaborationSidebarProvider",
      threshold: 2000,
      renderFallback: t0,
      children: t4
    });
    $[12] = props.appName;
    $[13] = t0;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}
function _temp2(t0) {
  const {
    collaborationSidebarState
  } = t0;
  return collaborationSidebarState.cleanup();
}